import React, { useMemo } from "react";

// ─── Layout constants ──────────────────────────────────────────────────────────
const SVG_SIZE   = 230;
const CX         = SVG_SIZE / 2;  // 115
const CY         = SVG_SIZE / 2;  // 115
const OUTER_R    = 100;
const INNER_R    = 68;            // 68 % of outer — matches Apex's donut size:"68%"
const DEFAULT_GAP_DEG = 1.2;     // angular gap between adjacent slices (degrees)

// ─── Helpers ──────────────────────────────────────────────────────────────────
function polarToCartesian(r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: CX + r * Math.cos(rad),
    y: CY + r * Math.sin(rad),
  };
}

function buildPath(startAngle, sweepDeg) {
  // Clamp to avoid degenerate full-circle paths
  const sweep    = Math.min(sweepDeg, 359.9999);
  const endAngle = startAngle + sweep;
  const large    = sweep > 180 ? 1 : 0;

  const o1 = polarToCartesian(OUTER_R, startAngle);
  const o2 = polarToCartesian(OUTER_R, endAngle);
  const i1 = polarToCartesian(INNER_R, endAngle);
  const i2 = polarToCartesian(INNER_R, startAngle);

  return [
    `M ${o1.x.toFixed(3)} ${o1.y.toFixed(3)}`,
    `A ${OUTER_R} ${OUTER_R} 0 ${large} 1 ${o2.x.toFixed(3)} ${o2.y.toFixed(3)}`,
    `L ${i1.x.toFixed(3)} ${i1.y.toFixed(3)}`,
    `A ${INNER_R} ${INNER_R} 0 ${large} 0 ${i2.x.toFixed(3)} ${i2.y.toFixed(3)}`,
    "Z",
  ].join(" ");
}

// ─── Component ────────────────────────────────────────────────────────────────
// Props:
//   series            number[]          – count per slice, same order as categoryNames
//   colors            string[]          – hex colour per slice
//   categoryNames     string[]          – label per slice
//   selectedCategories Set<string>      – which slices are active
//   onSelect          (name) => void    – called on slice click
//   onSliceMouseEnter (event, i) => void
//   onSliceMouseLeave () => void
//   size              number            – rendered pixel size (default 230)
const DonutChart = ({
  series,
  colors,
  categoryNames,
  selectedCategories,
  onSelect,
  onSliceMouseEnter,
  onSliceMouseLeave,
  size = 230,
  gapDeg = DEFAULT_GAP_DEG,
  sliceStrokeWidth = 2.5,
}) => {
  const total = useMemo(() => series.reduce((s, v) => s + (v || 0), 0), [series]);

  const slices = useMemo(() => {
    if (total === 0) return [];

    const nonZeroCount = series.filter((v) => v > 0).length;
    const totalGap     = gapDeg * nonZeroCount;
    const availDeg     = 360 - totalGap;

    let cursor = 0;
    return series
      .map((value, i) => {
        if (!value || value <= 0) return null;

        const sweep  = (value / total) * availDeg;
        const start  = cursor;
        cursor += sweep + gapDeg;

        // When a single slice covers the full circle, the arc start and end
        // points are identical and SVG silently drops the arc. Mark it so the
        // renderer can use a <circle> instead.
        const isFullCircle = nonZeroCount === 1;

        return {
          i,
          path: isFullCircle ? null : buildPath(start, sweep),
          isFullCircle,
        };
      })
      .filter(Boolean);
  }, [series, total, gapDeg]);

  const hasSelection = selectedCategories.size > 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
      style={{ display: "block", overflow: "visible" }}
    >
      {slices.map((slice) => {
        const name       = categoryNames[slice.i];
        const isSelected = selectedCategories.has(name);
        const opacity    = hasSelection && !isSelected ? 0.2 : 1;
        const sharedProps = {
          key:          name,
          fill:         slice.isFullCircle ? "none" : colors[slice.i],
          stroke:       colors[slice.i],
          style:        { opacity, transition: "opacity 0.18s ease", cursor: "pointer" },
          onClick:      () => onSelect(name),
          onMouseEnter: (e) => onSliceMouseEnter?.(e, slice.i),
          onMouseLeave: () => onSliceMouseLeave?.(),
        };

        if (slice.isFullCircle) {
          return (
            <circle
              {...sharedProps}
              cx={CX}
              cy={CY}
              r={(OUTER_R + INNER_R) / 2}
              strokeWidth={OUTER_R - INNER_R}
            />
          );
        }

        return (
          <path
            {...sharedProps}
            d={slice.path}
            fill={colors[slice.i]}
            stroke="#FFFFFF"
            strokeWidth={sliceStrokeWidth}
          />
        );
      })}
    </svg>
  );
};

export default DonutChart;
